import React, { useEffect, useMemo, useState } from "react";
import { qrEventsApi } from "../../api/qrEventsApi";

// MUI
import {
    Box,
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    TextField,
    Tooltip,
    Typography, CircularProgress,
} from "@mui/material";
import { notify } from "../../../../util/toast";
import FormModal from "../../../../component/common/FormModal";
import ResizableTable from "../../../../component/common/ResizableTable";
import ActionButton from "../../../../component/common/ActionButton";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import InputAdornment from "@mui/material/InputAdornment";
import { formatDateTime } from "../../../../util/dateUtils";
import { ds } from "../../../../styles/designSystem";
import FormField from "../../../../component/common/FormField";
import AppAutocomplete from "../../../../component/common/AppAutocomplete";
import { runApiAction } from "../../../../util/apiFeedback";
import TablePaginator from "../../../../component/common/TablePaginator";

const PAGE_SIZE = 100;

const emptyParticipant = {
    privateCode: "",
    name: "",
    affiliation: "",
    gender: "",
    contactNumber: "",
    email: "",
    attend_time: "",
};

/**
 * ✅ 상위에서 행사명을 내려주면(selectedEventFromParent) 자동으로 행사 선택 + 참가자 로드
 * - onSelectedEventChange: 사용자가 드롭다운에서 행사 바꿀 때 상위에도 반영
 */
export default function ParticipantTable({
                                             selectedEventFromParent = "",
                                             onSelectedEventChange,
                                         }) {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState("");
    const [participants, setParticipants] = useState([]);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages]   = useState(1);
    const [totalElements, setTotalElements] = useState(0);

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingParticipants, setLoadingParticipants] = useState(false);

    // 참가자 모달
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState("add"); // add | edit | delete
    const [currentParticipant, setCurrentParticipant] = useState(emptyParticipant);

    // xlsx 업로드 모달
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [file, setFile] = useState(null);
    const [uploadingXlsx, setUploadingXlsx] = useState(false);

    // 참가자 검색 — 이름·이메일·소속·연락처·직함 부분일치 (현재 페이지 내)
    const [participantSearch, setParticipantSearch] = useState("");


    const fetchEvents = async () => {
        setLoadingEvents(true);
        try {
            const response = await qrEventsApi.getEvents();
            setEvents(Array.isArray(response) ? response : (response?.data || response || []));
        } catch (error) {
            console.error("행사를 가져오는 중 오류", error);
            if (error.message?.includes("No permission") || error.message?.includes("권한")) {
                notify("행사 목록 확인 권한이 없습니다", "error");
            } else {
                notify(error.message || "행사 목록을 가져오는 중 오류가 발생했습니다.", "error");
            }
        } finally {
            setLoadingEvents(false);
        }
    };

    const fetchParticipants = async (eventName, page = 0, keyword = "") => {
        if (!eventName) return;
        setLoadingParticipants(true);
        try {
            // 검색어를 서버로 넘겨 전체 참가자에 걸쳐 매칭(현재 페이지만 필터하던 버그 해소)
            const response = await qrEventsApi.getParticipantsPaginated(eventName, page, PAGE_SIZE, keyword);
            const list = response?.content ?? (Array.isArray(response) ? response : (response?.data ?? []));
            setParticipants(list);
            setTotalPages(response?.totalPages ?? 1);
            setTotalElements(response?.totalElements ?? list.length);
            setSelectedEvent(eventName);
            setCurrentPage(page);
            onSelectedEventChange?.(eventName);
        } catch (error) {
            if (error.message?.includes("No permission") || error.message?.includes("권한")) {
                notify("참가자 목록 확인 권한이 없습니다", "error");
            } else {
                notify(error.message || "참가자 목록을 가져오는 중 오류가 발생했습니다.", "error");
            }
        } finally {
            setLoadingParticipants(false);
        }
    };

    useEffect(() => {
        fetchEvents();
        // eslint-disable-next-line
    }, []);

    // ✅ 상위에서 행사명이 들어오면 자동 선택 + 로드
    useEffect(() => {
        if (!selectedEventFromParent) return;
        if (selectedEventFromParent === selectedEvent) return;
        fetchParticipants(selectedEventFromParent, 0, participantSearch);
        // eslint-disable-next-line
    }, [selectedEventFromParent]);

    // 검색어 변경 → 디바운스 후 서버 재조회(page 0). 전체 참가자에 걸쳐 매칭되도록.
    // (sortedParticipants 의 클라이언트 필터는 디바운스 대기 중 현재 페이지에 즉시 반영되는 보조 역할)
    useEffect(() => {
        if (!selectedEvent) return;
        const t = setTimeout(() => { fetchParticipants(selectedEvent, 0, participantSearch); }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [participantSearch]);

    const selectedEventLabel = useMemo(() => selectedEvent || "(미선택)", [selectedEvent]);

    const [sort, setSort] = useState({ key: "name", dir: "asc" });

    const toggleSort = (key) =>
        setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

    const sortLabel = (key, label) =>
        `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;

    // 검색 + 정렬 적용. 검색은 키워드 부분일치 (이름·이메일·소속·연락처·직함·시트명).
    const sortedParticipants = useMemo(() => {
        const kw = String(participantSearch || "").trim().toLowerCase();
        const filtered = !kw
            ? (participants || [])
            : (participants || []).filter((p) => {
                const hay = [p.name, p.email, p.affiliation, p.contactNumber, p.title, p.sheetName, p.privateCode]
                    .map((v) => String(v || "").toLowerCase())
                    .join(" ");
                return hay.includes(kw);
            });
        return [...filtered].sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "ko");
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [participants, sort, participantSearch]);

    const openDialog = (mode, participant = emptyParticipant) => {
        if (!selectedEvent && mode === "add") {
            notify("참가자를 추가하려면 먼저 행사를 선택해주세요.", "warning");
            return;
        }
        setDialogMode(mode);
        setCurrentParticipant(participant);
        setOpenEditDialog(true);
    };

    const closeDialog = () => setOpenEditDialog(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentParticipant((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneNumberChange = (e) => {
        let value = e.target.value.replace(/[^0-9]/g, "");
        if (value.length > 3 && value.length <= 7) {
            value = `${value.slice(0, 3)}-${value.slice(3)}`;
        } else if (value.length > 7) {
            value = `${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7, 11)}`;
        }
        setCurrentParticipant((prev) => ({ ...prev, contactNumber: value }));
    };

    const validateParticipant = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(currentParticipant.email || "")) {
            notify("올바른 이메일 형식을 입력해주세요.", "warning");
            return false;
        }

        const phoneRegex = /^010-\d{4}-\d{4}$/;
        if (!phoneRegex.test(currentParticipant.contactNumber || "")) {
            notify("전화번호는 010-0000-0000 형식으로 입력해주세요.", "warning");
            return false;
        }

        const required = ["name", "affiliation", "gender", "contactNumber", "email"];
        for (const k of required) {
            if (!String(currentParticipant[k] || "").trim()) {
                notify("모든 필드를 채워주세요.", "warning");
                return false;
            }
        }
        return true;
    };

    const handleSave = () => {
        if (!validateParticipant()) return;

        const isAdd = dialogMode === "add";
        return runApiAction({
            request: () => isAdd
                ? qrEventsApi.registerParticipant({ eventName: selectedEvent, participant: currentParticipant })
                : qrEventsApi.updateParticipant({ eventName: selectedEvent, participant: currentParticipant }),
            successMsg: isAdd ? "참가자가 추가되었습니다." : "참가자가 수정되었습니다.",
            errorMsg: "존재하지 않는 행사이거나 중복 참가자(이메일+행사) 입니다.",
            onSuccess: async () => {
                await fetchParticipants(selectedEvent, currentPage);
                closeDialog();
            },
            onError: (error) => console.error("저장 오류", error),
        });
    };

    const handleDelete = () => runApiAction({
        request: () => qrEventsApi.deleteParticipant(currentParticipant.privateCode),
        successMsg: "참가자가 삭제되었습니다.",
        errorMsg: "참가자 삭제 중 오류가 발생했습니다.",
        onSuccess: async () => {
            await fetchParticipants(selectedEvent, currentPage);
            closeDialog();
        },
        onError: (error) => console.error("삭제 오류", error),
    });

    const handleOpenUpload = () => {
        if (!selectedEvent) {
            notify("xlsx 파일을 업로드하려면 먼저 행사를 선택해주세요.", "warning");
            return;
        }
        setFile(null);
        setUploadingXlsx(false);
        setOpenUploadDialog(true);
    };

    const handleUploadXlsx = async () => {
        if (uploadingXlsx) return;
        if (!file) {
            notify("xlsx 파일을 선택해주세요.", "warning");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("eventName", selectedEvent);

        await runApiAction({
            request: () => qrEventsApi.uploadParticipants(selectedEvent, file),
            errorMsg: "xlsx 파일 업로드 중 오류가 발생했습니다.",
            successMsg: (data) => typeof data === "string" ? data : data?.message || "업로드 완료",
            setLoading: setUploadingXlsx,
            onSuccess: async () => {
                await fetchParticipants(selectedEvent, currentPage);
                setOpenUploadDialog(false);
            },
            onError: (error) => console.error("업로드 오류", error),
        });
    };

    const handleDownloadXlsx = async () => {
        if (!selectedEvent) {
            notify("다운로드하려면 먼저 행사를 선택해주세요.", "warning");
            return;
        }

        try {
            const res = await qrEventsApi.downloadParticipants(selectedEvent);
            const blob = new Blob([res instanceof Blob ? res : (res?.data ?? res)], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${selectedEvent}_participants.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("다운로드 오류", error);
            notify("참가자 xlsx 다운로드 중 오류가 발생했습니다.", "error");
        }
    };

    const handlePageChange = (newPage) => {
        // TablePaginator는 1-based, API는 0-based. 현재 검색어를 유지한 채 페이지 이동.
        fetchParticipants(selectedEvent, newPage - 1, participantSearch);
    };


    return (
        <Box>
            {/* 상단 컨트롤 */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-end" }}>
                    <FormField label="행사 선택 (행사명 검색 가능)" required>
                        {/* 사내 공용 AppAutocomplete 로 통일 — 행사 수가 늘어도 검색으로 빠르게 찾도록.
                            value 는 객체(event) 로 controlled, selectedEvent state 는 그대로 eventName 문자열 유지
                            (다른 호출처들이 string eventName 을 사용하므로 변경 최소화). */}
                        <AppAutocomplete
                            options={events}
                            value={events.find((e) => e.eventName === selectedEvent) || null}
                            onChange={(v) => {
                                const name = v && typeof v !== "string" ? v.eventName : "";
                                if (name) fetchParticipants(name, 0, participantSearch);
                            }}
                            getOptionLabel={(o) => (typeof o === "string" ? o : (o?.eventName || ""))}
                            isOptionEqualToValue={(a, b) => {
                                const an = typeof a === "string" ? a : a?.eventName;
                                const bn = typeof b === "string" ? b : b?.eventName;
                                return an === bn;
                            }}
                            disabled={loadingEvents}
                            placeholder="행사를 선택하세요"
                            variant="outlined" disableUnderline={false}
                            sx={{ minWidth: 280 }}
                        />
                    </FormField>

                    <Typography sx={{ ...ds.typography.caption, pb: 0.8 }}>
                        선택된 행사: <b>{selectedEventLabel}</b>
                    </Typography>

                    {(loadingEvents || loadingParticipants) && <CircularProgress size={22} />}
                </Stack>

                <Stack direction="row" spacing={1}>
                    <ActionButton variant="download" onClick={handleDownloadXlsx} disabled={!selectedEvent}
                        label="전체 xlsx 다운로드" />
                    <ActionButton variant="upload" onClick={handleOpenUpload} disabled={!selectedEvent}
                        label="참가자 xlsx로 추가" />
                    <ActionButton variant="add" startIcon={<PersonAddAlt1Icon sx={{ fontSize: 16 }} />}
                        onClick={() => openDialog("add", emptyParticipant)} disabled={!selectedEvent}
                        label="참가자 추가" />
                </Stack>
            </Stack>

            {/* 테이블 */}
            <Paper variant="outlined" sx={{ ...ds.card, mt: 2, overflow: "hidden" }}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
                        <Typography sx={ds.typography.sectionTitle}>참가자 목록</Typography>
                        <Box sx={{ flex: 1 }} />
                        <TextField
                            size="small"
                            placeholder="이름·이메일·소속·연락처·직함 검색"
                            value={participantSearch}
                            onChange={(e) => setParticipantSearch(e.target.value)}
                            disabled={!selectedEvent}
                            sx={{ width: { xs: "100%", sm: 320 } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: participantSearch ? (
                                    <InputAdornment position="end">
                                        <Tooltip title="검색어 지우기">
                                            <IconButton size="small"
                                                onClick={() => setParticipantSearch("")}
                                                sx={{ p: 0.5 }}>
                                                <ClearIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        {participantSearch && (
                            <Typography sx={{ ...ds.typography.caption, whiteSpace: "nowrap" }}>
                                {totalElements}건 매칭
                            </Typography>
                        )}
                    </Stack>
                </Box>
                <Divider />
                <TableContainer>
                    <ResizableTable size="small" sx={ds.tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                        <span>개인코드</span>
                                        <Tooltip title="QR 코드와 동일한 값이에요">
                                            <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>{sortLabel("name", "이름")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("affiliation")}>{sortLabel("affiliation", "소속")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("gender")}>{sortLabel("gender", "성별")}</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("contactNumber")}>{sortLabel("contactNumber", "연락처")}</TableCell>
                                <TableCell>시트명</TableCell>
                                <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("email")}>{sortLabel("email", "이메일")}</TableCell>
                                <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("attendTime")}>
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                        <span>{sortLabel("attendTime", "참석 시간")}</span>
                                        <Tooltip title="최초 참석 시간을 의미해요">
                                            <HelpOutlineIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                                <TableCell align="center">관리</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {sortedParticipants.map((p, idx) => (
                                <TableRow key={`${p.privateCode}-${idx}`} hover>
                                    <TableCell align="center">{p.privateCode}</TableCell>
                                    <TableCell align="center">{p.name}</TableCell>
                                    <TableCell align="center">{p.affiliation}</TableCell>
                                    <TableCell align="center">{p.gender}</TableCell>
                                    <TableCell align="center">{p.contactNumber}</TableCell>
                                    <TableCell align="center">{p.sheetName}</TableCell>
                                    <TableCell align="center">{p.email}</TableCell>
                                    <TableCell align="center">{formatDateTime(p.attendTime)}</TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <ActionButton variant="edit" onClick={() => openDialog("edit", p)} />
                                            <ActionButton variant="delete" onClick={() => openDialog("delete", p)} />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {sortedParticipants.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                        {!selectedEvent
                                            ? "행사를 선택해주세요."
                                            : participantSearch
                                                ? "검색 조건에 맞는 참가자가 없습니다."
                                                : "참가자가 없습니다."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </ResizableTable>
            <TablePaginator
                totalPages={totalPages}
                total={totalElements}
                pageSize={PAGE_SIZE}
                page={currentPage + 1}
                onPageChange={handlePageChange}
            />
                </TableContainer>
            </Paper>

            {/* 페이지네이션 */}

            {/* xlsx 업로드 다이얼로그 */}
            <FormModal
                open={openUploadDialog}
                onClose={() => setOpenUploadDialog(false)}
                title="xlsx 파일 업로드"
                subtitle={`선택된 행사: ${selectedEventLabel}`}
                maxWidth="sm"
                onSave={handleUploadXlsx}
                saving={uploadingXlsx}
                saveLabel={uploadingXlsx ? "업로드 중" : "업로드"}
                saveDisabled={!file}
            >
                <Stack spacing={1.5}>
                    <ActionButton variant="secondary"
                        component="label"
                        startIcon={<UploadFileIcon sx={{ fontSize: 16 }} />}
                        disabled={uploadingXlsx}>
                        <Box component="span">파일 선택 (.xlsx)</Box>
                        <input hidden type="file" accept=".xlsx" disabled={uploadingXlsx} onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </ActionButton>

                    <Typography variant="body2">
                        {file ? `선택된 파일: ${file.name}` : "선택된 파일 없음"}
                    </Typography>
                </Stack>
            </FormModal>

            {/* 참가자 추가/수정/삭제 다이얼로그 */}
            <FormModal
                open={openEditDialog}
                onClose={closeDialog}
                title={dialogMode === "add" ? "참가자 추가" : dialogMode === "edit" ? "참가자 수정" : "참가자 삭제"}
                maxWidth="sm"
                onSave={dialogMode === "delete" ? handleDelete : handleSave}
                saveLabel={dialogMode === "delete" ? "삭제" : "저장"}
                saveColor={dialogMode === "delete" ? "danger" : "primary"}
            >
                {dialogMode === "delete" ? (
                    <Typography>
                        정말로 <b>{currentParticipant.name}</b> 참가자를 삭제하시겠습니까?
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        <FormField label="이름" required>
                            <TextField name="name" size="small" value={currentParticipant.name || ""} onChange={handleInputChange} placeholder="이름을 입력하세요" fullWidth />
                        </FormField>
                        <FormField label="소속" required>
                            <TextField name="affiliation" size="small" value={currentParticipant.affiliation || ""} onChange={handleInputChange} placeholder="소속을 입력하세요" fullWidth />
                        </FormField>
                        <FormField label="성별" required>
                            <TextField name="gender" size="small" value={currentParticipant.gender || ""} onChange={handleInputChange} placeholder="Male / Female / Other" fullWidth />
                        </FormField>
                        <FormField label="연락처" required>
                            <TextField name="contactNumber" size="small" value={currentParticipant.contactNumber || ""} onChange={handlePhoneNumberChange} placeholder="010-0000-0000" fullWidth />
                        </FormField>
                        <FormField label="이메일" required>
                            <TextField name="email" size="small" value={currentParticipant.email || ""} onChange={handleInputChange} placeholder="이메일을 입력하세요" fullWidth />
                        </FormField>
                    </Stack>
                )}
            </FormModal>

        </Box>
    );
}
